import { useRef, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  createTemplate,
  updateTemplate,
} from "../../../services/smsTemplateServices";
import { Minus, Plus } from "lucide-react";

const SmsCreateTemplateModal = ({
  onClose,
  editMode = false,
  templateData = null,
}) => {
  const modalRef = useRef(null);
  const [templateName, setTemplateName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [placeholders, setPlaceholders] = useState([
    { position: 1, label: "" },
  ]);
  const [messageMode, setMessageMode] = useState("static");
  const [sampleMessage, setSampleMessage] = useState("");

  useEffect(() => {
    if (editMode && templateData) {
      setTemplateName(templateData.templateName || "");
      setTemplateId(templateData.templateId || "");
      setSampleMessage(templateData.sampleMessage || "");

      if (templateData.placeholders && templateData.placeholders.length > 0) {
        setMessageMode("dynamic");
        setPlaceholders(templateData.placeholders);
      } else {
        setMessageMode("static");
        setPlaceholders([{ position: 1, label: "" }]);
      }
    }
  }, [editMode, templateData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleAddPlaceholder = () => {
    const nextPosition =
      placeholders.length > 0
        ? Math.max(...placeholders.map((p) => p.position)) + 1
        : 1;
    setPlaceholders([...placeholders, { position: nextPosition, label: "" }]);
  };

  const handleRemovePlaceholder = (index) => {
    const updated = [...placeholders];
    updated.splice(index, 1);
    setPlaceholders(updated);
  };

  const handlePlaceholderChange = (index, value) => {
    const updated = [...placeholders];
    updated[index].label = value;
    setPlaceholders(updated);
  };

  const handleSubmit = async () => {
    if (!templateId.trim() || !templateName.trim()) {
      toast.error("Template ID and Name are required");
      return;
    }

    if (
      messageMode === "dynamic" &&
      placeholders.some((p) => !p.label.trim())
    ) {
      toast.error("All placeholders must have labels");
      return;
    }

    const payload = {
      templateName: templateName.trim(),
      templateId: templateId.trim(),
      sampleMessage: sampleMessage,
      placeholders: messageMode === "dynamic" ? placeholders : [],
    };

    setIsLoading(true);

    try {
      if (editMode && templateData) {
        await toast.promise(updateTemplate(templateData._id, payload), {
          loading: "Updating template...",
          success: "Template updated successfully!",
          error: (err) => err.message || "Failed to update template",
        });
      } else {
        await toast.promise(createTemplate(payload), {
          loading: "Creating template...",
          success: "Template created successfully!",
          error: (err) => err.message || "Failed to create template",
        });
      }
      onClose();
    } catch (error) {
      console.error("Template operation failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white relative w-full animate-scale-in max-w-2xl max-h-[90vh] overflow-auto purple-scroll rounded-[20px] shadow-lg"
      >
        <h2 className="text-center text-blue font-semibold text-sm mb-4 mt-8">
          {editMode ? "Update Template" : "Create Template"}
        </h2>

        <div className="border-b-[0.5px] border-[#B3AEAE] w-full"></div>

        <div
          onClick={onClose}
          aria-label="Close"
          className="absolute right-5 z-50 top-3 cursor-pointer text-black text-base font-bold !focus:outline-none"
        >
          ✕
        </div>

        <div className="space-y-4 p-4">
          <div>
            <label className="block text-sm font-medium text-black mb-[11px]">
              Template Name
            </label>
            <input
              type="text"
              className="w-full border border-[#0000003D] !focus:outline-none bg-[#FAFAFA] rounded-[10px] px-3 h-[48px] text-sm"
              placeholder="e.g. Order Confirmation"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-[11px]">
              Template Id
            </label>
            <input
              type="text"
              className="w-full border border-[#0000003D] !focus:outline-none bg-[#FAFAFA] rounded-[10px] px-3 h-[48px] text-sm"
              placeholder="e.g. Order Confirmation"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              disabled={editMode}
            />
          </div>

          <div className="flex mb-4 justify-normal flex-col items-start relative w-full">
            <p className="text-sm font-medium text-black mb-[11px]">
              Message Mode
            </p>
            <div className="flex gap-3 whitespace-nowrap">
              {["static", "dynamic"].map((val) => (
                <label
                  key={val}
                  className="flex items-center cursor-pointer relative"
                >
                  <input
                    type="radio"
                    name="messageMode"
                    value={val}
                    checked={messageMode === val}
                    onChange={(e) => setMessageMode(e.target.value)}
                    className="appearance-none sr-only"
                  />
                  <div className="w-5 h-5 absolute left-0 rounded-full bg-[#d9d9d9] flex items-center justify-center">
                    <div
                      className={`w-3 h-3 rounded-full transition ${
                        messageMode === val ? "bg-[#4362C1]" : "bg-[#C0C4D1]"
                      }`}
                    />
                  </div>
                  <span className="ml-2 text-xs font-medium text-black">
                    {val === "static" ? "Static Message" : "Dynamic Message"}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {messageMode === "dynamic" && (
            <div className="w-full">
              <label className="text-sm font-medium text-black">
                Placeholders
              </label>
              <div className="mt-2">
                {placeholders.map((ph, index) => (
                  <div key={index} className="flex items-end mb-4 gap-2 w-full">
                    <div className="flex flex-col items-start gap-y-2 w-full">
                      <span className="text-xs font-medium text-black">
                        Variable Position - {ph.position}
                      </span>
                      <input
                        type="text"
                        className="border border-[#0000003D] w-full focus:outline-0 bg-[#FAFAFA] rounded-[10px] px-3 h-[44px] text-sm"
                        placeholder="Placeholder Value"
                        value={ph.label}
                        onChange={(e) =>
                          handlePlaceholderChange(index, e.target.value)
                        }
                      />
                    </div>
                    {placeholders.length > 1 && (
                      <button
                        type="button"
                        className="h-[44px] cursor-pointer"
                        onClick={() => handleRemovePlaceholder(index)}
                      >
                        <Minus className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                    {index === placeholders.length - 1 && (
                      <button
                        type="button"
                        className="flex items-center h-[44px] cursor-pointer gap-1 text-sm text-[#4F22D8]"
                        onClick={handleAddPlaceholder}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-black mb-[11px]">
              Sample Message
            </label>
            <textarea
              className="w-full border border-[#0000003D] bg-[#FAFAFA] rounded-[10px] px-3 py-[10px] h-24 text-sm"
              onChange={(e) => setSampleMessage(e.target.value)}
              value={sampleMessage}
            />
          </div>

          <div
            onClick={handleSubmit}
            className="w-full bg-[#4F22D8] flex justify-center items-center text-white rounded-[10px] py-[10px] text-sm cursor-pointer disabled:opacity-50"
          >
            {isLoading
              ? "Processing..."
              : editMode
              ? "Update Template"
              : "Create Template"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsCreateTemplateModal;
